'use client';

import { useTranslations } from 'next-intl';
import { useContext, useEffect, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { CartContext } from '@/entities/cart';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { useTelephoneVerification } from '@/features/auth/lib/useTelephoneVerification';
import { EventService } from '@/shared/lib/event-service';
import { isTelephoneValid } from '@/shared/lib/validations';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Form } from '@/shared/ui/form';
import { Icon } from '@/shared/ui/icon';
import { Popover } from '@/shared/ui/popover';

/**
 * Badge / verify button only. Kept as its own leaf so verification query updates
 * don't re-render Form.TelephoneField (input) or the rest of the cart.
 */
const CartPhoneVerifyActions = () => {
    const t = useTranslations();
    const { formMethods } = useContext(CartContext);
    const [telephone, setTelephone] = useState(() => formMethods.getValues('contactInfo.telephone') ?? '');
    // subscribe() does not mark the field as watched for useForm root re-renders
    // (unlike useWatch). Debounce before hitting the verification status API.
    const [debouncedTelephone] = useDebounceValue(telephone, 400);

    useEffect(() => {
        return formMethods.subscribe({
            name: 'contactInfo.telephone',
            exact: true,
            formState: { values: true },
            callback: ({ values }) => {
                setTelephone(values.contactInfo?.telephone ?? '');
            },
        });
    }, [formMethods]);

    const { isVerified, nextRetryAt, startVerification } = useTelephoneVerification(debouncedTelephone, {
        fallbackToLocal: true,
        enabled: true,
    });

    // Live validity for the button (don't wait for debounce); status query uses debounced value.
    const showVerify = isTelephoneValid(telephone) && isVerified !== true;

    const handleVerifyPhone = async () => {
        const value = formMethods.getValues('contactInfo.telephone');
        if (nextRetryAt && new Date(nextRetryAt).getTime() > Date.now()) {
            EventService.trigger('phone-verification:start', {
                telephone: value,
                nextRetryAt,
                fallbackToLocal: true,
            });
            return;
        }
        const response = await startVerification.mutateAsync(value);
        EventService.trigger('phone-verification:start', {
            telephone: value,
            nextRetryAt: response.nextRetryAt,
            fallbackToLocal: true,
        });
    };

    if (isVerified === true) {
        return (
            <div className="cart-customer-data-form-box__phone-actions">
                <Badge
                    variant="success"
                    appearance="outline"
                    className="cart-customer-data-form-box__verified-badge"
                    data-testid="phone-verified-badge"
                >
                    <Icon name="check" />
                    {t('CART_CUSTOMER_DATA_FORM_BOX.VERIFIED_BADGE')}
                </Badge>
            </div>
        );
    }

    if (!showVerify) {
        return null;
    }

    return (
        <div className="cart-customer-data-form-box__phone-actions">
            <Button
                className="cart-customer-data-form-box__verify-button"
                data-testid="verify-phone-button"
                size="small"
                onClick={handleVerifyPhone}
                disabled={startVerification.isPending || !isTelephoneValid(telephone)}
            >
                {t('CART_CUSTOMER_DATA_FORM_BOX.VERIFY_BUTTON')}
            </Button>
        </div>
    );
};

export const CartCustomerPhoneField = () => {
    const t = useTranslations();
    const { verifyTelephoneEnabled } = useContext(AppConfigContext);

    return (
        <>
            <Form.TelephoneField
                name="contactInfo.telephone"
                label={t('CART_CUSTOMER_DATA_FORM_BOX.PHONE_LABEL')}
                data-testid="telephone-input"
                rules={{
                    required: true,
                    validate: {
                        isValidTelephone: (value) =>
                            isTelephoneValid(value) ||
                            t('VALIDATIONS.IS_NOT_IN_VALID_FORMAT', {
                                name: t('WORDS.TELEPHONE'),
                            }),
                    },
                }}
                right={verifyTelephoneEnabled ? <CartPhoneVerifyActions /> : undefined}
            />
            {verifyTelephoneEnabled && (
                <Popover>
                    <Popover.Trigger asChild>
                        <button
                            type="button"
                            className="cart-customer-data-form-box__help-text"
                            data-testid="phone-verification-help"
                        >
                            {t('CART_CUSTOMER_DATA_FORM_BOX.WHY_VERIFY_LINK')}
                        </button>
                    </Popover.Trigger>
                    <Popover.Content
                        className="cart-customer-data-form-box__popover"
                        side="right"
                        avoidCollisions={false}
                    >
                        <Popover.Arrow />
                        <div className="cart-customer-data-form-box__popover-header">
                            <Icon name="phone" width={24} height={24} />
                            <b className="cart-customer-data-form-box__popover-title">
                                {t('CART_CUSTOMER_DATA_FORM_BOX.VERIFICATION_POPOVER_TITLE')}
                            </b>
                        </div>
                        <p className="cart-customer-data-form-box__popover-text">
                            {t('CART_CUSTOMER_DATA_FORM_BOX.VERIFICATION_POPOVER_TEXT')}
                        </p>
                    </Popover.Content>
                </Popover>
            )}
        </>
    );
};
